import React, { useState, useEffect, useMemo } from 'react';
import './AdminDashboard.css';
import './MessagesChat.css';
import MessagesChat from './MessagesChat';
import { resolveApplicantAccess } from '../utils/applicantAccess';
import { filterDeletedMessages, getConversationId, groupApplicantMessages } from '../utils/messageConversations';
import { getT, formatT } from '../i18n';
import { API_URL } from '../config/api';

const buildMissingDocsEmailMessage = (name, issuesList) => (
  `Hello ${name},\n\nThank you for submitting your application with Helport. We have reviewed your submission and found that the following required documents are missing or incomplete:\n\n${issuesList.map((issue) => `• ${issue}`).join('\n')}\n\nTo proceed with your application, please log in to your account and update your application form with the correct information for the above items.\n\n⚠️ IMPORTANT: Your salary will be placed on hold until these required documents are properly submitted.\n\nIf you have already submitted this information and believe this is an error, or if you need any clarification, please reply to this message and we will be happy to assist you.\n\nBest regards,\nHelport HR Team`
);

const ApplicantDashboard = ({ currentUser, messages = [], application = null, onReplyMessage, onEditChatMessage, onDeleteChatMessage, hiddenMessageIds, onGoToApply, onBack, lang = 'en', variant = 'page' }) => {
  const userEmail = currentUser?.email || currentUser?.username || '';
  const userName = currentUser?.full_name || currentUser?.name || '';

  const [selectedMessageId, setSelectedMessageId] = useState(() => (
    userEmail ? getConversationId(userEmail) : null
  ));
  const [replyInput, setReplyInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [emailNotifyState, setEmailNotifyState] = useState('idle');

  const t = getT(lang);
  const ta = t.applicant;

  const applicantAccess = useMemo(
    () => resolveApplicantAccess(userEmail, messages, application),
    [userEmail, messages, application]
  );

  const pendingResubmit = applicantAccess.mode === 'resubmit' ? applicantAccess.resubmit : null;
  const isWaiting = applicantAccess.mode === 'waiting';
  const resubmitStep = pendingResubmit?.step || 1;
  const missingDocuments = pendingResubmit?.missingDocuments || [];

  const goToResubmitStep = () => {
    if (typeof onGoToApply === 'function') {
      onGoToApply(resubmitStep);
    }
  };

  const handleMissingEmailNotify = async () => {
    if (!userEmail || missingDocuments.length === 0 || emailNotifyState === 'sending') return;

    const displayName = userName || 'Applicant';
    setEmailNotifyState('sending');

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'send_missing_document_notification',
          applicant_email: userEmail,
          applicant_name: displayName,
          applicant_id: application?.id || null,
          missing_documents: missingDocuments,
          message: buildMissingDocsEmailMessage(displayName, missingDocuments),
        }),
      });

      const result = await response.json();

      if (result.status === 'success' && result.email_sent) {
        setEmailNotifyState('sent');
        window.setTimeout(() => setEmailNotifyState('idle'), 4000);
        return;
      }

      setEmailNotifyState('error');
      window.alert(result.message || ta.missingEmailFailed);
      window.setTimeout(() => setEmailNotifyState('idle'), 3000);
    } catch (error) {
      console.error('Error sending missing documents email:', error);
      setEmailNotifyState('error');
      window.alert(ta.missingEmailFailed);
      window.setTimeout(() => setEmailNotifyState('idle'), 3000);
    }
  };

  const userMessages = useMemo(() => {
    const filtered = filterDeletedMessages(messages).filter((msg) => {
      const normalizedMsgEmail = (msg.email || '').trim().toLowerCase();
      const normalizedUserEmail = (userEmail || '').trim().toLowerCase();
      const matchEmail = normalizedMsgEmail !== '' && normalizedMsgEmail === normalizedUserEmail;
      const matchName = (msg.name || '').trim().toLowerCase() === (userName || '').trim().toLowerCase();
      return matchEmail || matchName;
    });
    return groupApplicantMessages(filtered, userEmail);
  }, [messages, userEmail, userName]);

  const chatMessages = useMemo(() => {
    if (userMessages.length > 0) return userMessages;
    if (!userEmail) return [];
    return [{
      id: getConversationId(userEmail),
      name: 'Admin',
      email: userEmail,
      message: '',
      createdAt: new Date().toISOString(),
      replies: [],
      _sourceMessages: []
    }];
  }, [userMessages, userEmail]);

  useEffect(() => {
    if (!userEmail) {
      setSelectedMessageId(null);
      return;
    }
    const defaultId = getConversationId(userEmail);
    if (!chatMessages.find((m) => String(m.id) === String(selectedMessageId))) {
      setSelectedMessageId(chatMessages[0]?.id || defaultId);
    }
  }, [chatMessages, selectedMessageId, userEmail]);

  const handleSubmitReply = async () => {
    const text = replyInput.trim();
    if (!text || !selectedMessageId) return;
    if (typeof onReplyMessage === 'function') {
      const sent = await onReplyMessage(selectedMessageId, text);
      if (sent !== false) setReplyInput('');
    }
  };

  const waitingBanner = isWaiting ? (
    <div className="applicant-waiting-banner">
      <h3>{ta.waitingBannerTitle}</h3>
      <p>{ta.waitingBannerText}</p>
    </div>
  ) : null;

  const missingDocsAlert = missingDocuments.length > 0 ? (
    <div className="missing-docs-alert" role="alert">
      <div className="missing-docs-alert__header">
        <div className="missing-docs-alert__icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <div className="missing-docs-alert__headline">
          <div className="missing-docs-alert__title-row">
            <h3>{ta.missingTitle}</h3>
            <span className="missing-docs-alert__badge">{missingDocuments.length}</span>
            <div className="missing-docs-alert__actions">
              <button
                type="button"
                className="missing-docs-notify-btn"
                onClick={handleMissingEmailNotify}
                disabled={emailNotifyState === 'sending'}
                title={ta.missingNotifyHint}
              >
                {emailNotifyState === 'sending'
                  ? ta.missingEmailSending
                  : emailNotifyState === 'sent'
                    ? ta.missingEmailSent
                    : ta.missingNotifyBtn}
              </button>
              <button
                type="button"
                className="missing-docs-btn"
                onClick={goToResubmitStep}
              >
                {formatT(ta.goToStepShort, { n: resubmitStep })}
              </button>
            </div>
          </div>
          <p className="missing-docs-alert__intro">{ta.missingIntro}</p>
        </div>
      </div>
      <div className="missing-docs-alert__docs">
        <p className="missing-docs-alert__docs-label">{ta.missingDocsListLabel}</p>
        <ul className="missing-docs-alert__list">
          {missingDocuments.map((doc, idx) => (
            <li key={idx} className="missing-docs-alert__doc-item">
              <span className="missing-docs-alert__doc-icon" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                  <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </span>
              <span className="missing-docs-alert__doc-text">{doc}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  ) : null;

  return (
    <div className={`tab-container messages-page applicant-messages${variant === 'widget' ? ' applicant-messages--widget' : ''}`}>
      <MessagesChat
        lang={lang}
        title={ta.myMessages}
        subtitle={ta.subtitle}
        messages={chatMessages}
        selectedMessageId={selectedMessageId}
        onSelectMessage={setSelectedMessageId}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        replyInput={replyInput}
        onReplyChange={setReplyInput}
        onSubmitReply={handleSubmitReply}
        mode="applicant"
        currentUserName={userName}
        onEditChatMessage={onEditChatMessage}
        onDeleteChatMessage={onDeleteChatMessage}
        hiddenMessageIds={hiddenMessageIds}
        beforeBody={(
          <>
            {pendingResubmit && missingDocuments.length === 0 && (
              <div className="applicant-messages-actions">
                <button
                  type="button"
                  className="applicant-messages-cta"
                  onClick={goToResubmitStep}
                >
                  {formatT(ta.goToStep, { n: resubmitStep })}
                </button>
              </div>
            )}
            {waitingBanner}
            {missingDocsAlert}
          </>
        )}
      />
    </div>
  );
};

export default ApplicantDashboard;
